// SentItemList — one row per item, for its whole life (Q2).
//
// SHARED, deliberately: Sent Items and the household file are two paths to the
// same list, so they are the same component with different data, not two
// tables that will drift. The household page passes `expandedIds` +
// `renderExpanded` to open its in-motion rows inline (§8.3); Sent Items passes
// neither and opens the panel in a drawer.
//
// The one rule this component will not break: IT ORDERS NOTHING. The server
// sorts per view (§6.1) and a client-side re-sort would interleave pages the
// moment "Load older" is pressed — so `items` renders in the order given, full
// stop. Filtering is the same story: what you see is what the server matched.
//
// Keyboard (§6.3, ported from AgreementsPage): ↑/↓ move, Enter opens the
// panel, `o` opens Full Details, and Escape belongs to the drawer. j/k are
// kept alongside ↑/↓ because that is what the Agreements page trained people
// to press and this list replaces it.

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignalChips, StatusChip } from '@/components/oc/bits';
import { RESTRICTED_ROW_NOTE } from '@/lib/viewDocs';
import { SentItemIcon } from '@/components/sentItems/SentItemIcon';
import {
  absoluteTime,
  glyphForKind,
  relativeTime,
  rowStateTone,
  waitingLabel,
  whereLabel,
} from '@/components/sentItems/sentItemsView';
import type { SentItemRow, SentItemsView } from '@/lib/sentItemsClient';

/** The fixed columns, named once so the expanded row's colSpan cannot drift.
 *  A caller that renders per-row actions adds one more (see `renderRowActions`). */
const COLUMN_COUNT = 6;

/** True when the event target is a place where typing should win over j/k. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export interface SentItemListProps {
  /** Rendered in the order given. This component never sorts (§6.1). */
  items: SentItemRow[];
  /** Which view produced this page — stamped on the table for styling only. */
  view?: SentItemsView;
  onOpen: (itemId: string) => void;
  /** Rows whose details are shown inline underneath them (§8.3). */
  expandedIds?: readonly string[];
  renderExpanded?: (row: SentItemRow) => React.ReactNode;
  emptyText: string;
  /** A panel is already open — Enter must not re-open it under the drawer. */
  panelOpen?: boolean;
  /**
   * Per-row affordances in a trailing column — the household file's §8.3
   * requirement that a FINISHED row stay one line while still offering its
   * Download and Full Details.
   *
   * ADDED BY W6, additively: passing nothing renders exactly the table Sent
   * Items already rendered, and a withheld row never gets one (it is not a
   * door — T5). Clicks inside the cell do not open the row.
   */
  renderRowActions?: (row: SentItemRow) => React.ReactNode;
}

export function SentItemList({
  items,
  view,
  onOpen,
  expandedIds,
  renderExpanded,
  emptyText,
  panelOpen = false,
  renderRowActions,
}: SentItemListProps): React.JSX.Element {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, items.length - 1)));
  }, [items.length]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openFullDetails = useCallback(
    (itemId: string) => navigate(`/items/${encodeURIComponent(itemId)}`),
    [navigate],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (items.length === 0) return;
      const row = items[activeIndex];
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, items.length - 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        // A withheld row opens onto a refusal — the row is not a door (T5).
        if (panelOpen || !row || row.redacted) return;
        e.preventDefault();
        onOpen(row.itemId);
      } else if (e.key === 'o') {
        if (!row || row.redacted) return;
        e.preventDefault();
        openFullDetails(row.itemId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, activeIndex, panelOpen, onOpen, openFullDetails]);

  if (items.length === 0) {
    return <div className="oc-empty">{emptyText}</div>;
  }

  const expanded = new Set(expandedIds ?? []);
  const columnCount = COLUMN_COUNT + (renderRowActions ? 1 : 0);

  return (
    <div className="table-wrap si-table-wrap">
      <table className="table si-table" data-view={view}>
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Item</th>
            <th scope="col">State</th>
            <th scope="col">Waiting on</th>
            <th scope="col">Last activity</th>
            <th scope="col">Signals</th>
            {renderRowActions && (
              <th scope="col" className="col-action">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => {
            // PRESENT BUT NOT YOURS. Type only — no person, no property, no
            // digest, no state, no date — and not clickable, because opening
            // it could only refuse. The list route omits restricted rows
            // entirely; this is the net under that (§5.7).
            if (row.redacted) {
              return (
                <tr key={row.itemId} className="si-row" data-withheld="true">
                  <td data-label="Type">
                    <span className="si-kind">
                      <SentItemIcon glyph={glyphForKind(row.kind)} />
                      <span className="pill" data-tone="neutral">
                        {row.kindLabel}
                      </span>
                    </span>
                  </td>
                  <td data-label="Item">
                    <StatusChip tone="closed">{RESTRICTED_ROW_NOTE}</StatusChip>
                  </td>
                  <td data-label="State" aria-hidden="true">
                    —
                  </td>
                  <td data-label="Waiting on" aria-hidden="true">
                    —
                  </td>
                  <td data-label="Last activity" aria-hidden="true">
                    —
                  </td>
                  <td data-label="Signals" aria-hidden="true">
                    —
                  </td>
                  {renderRowActions && (
                    <td className="col-action" aria-hidden="true">
                      —
                    </td>
                  )}
                </tr>
              );
            }

            const where = whereLabel(row);
            const isExpanded = expanded.has(row.itemId);
            return (
              <Fragment key={row.itemId}>
                <tr
                  className="si-row"
                  ref={(el) => {
                    rowRefs.current[i] = el;
                  }}
                  data-active={i === activeIndex ? 'true' : undefined}
                  data-blocked={row.lifecycle.blocked ? 'true' : undefined}
                  data-clickable="true"
                  aria-expanded={renderExpanded ? isExpanded : undefined}
                  onClick={() => {
                    setActiveIndex(i);
                    onOpen(row.itemId);
                  }}
                >
                  <td data-label="Type">
                    <span className="si-kind">
                      <SentItemIcon glyph={glyphForKind(row.kind)} />
                      <span className="pill" data-tone={row.isRenewal ? 'accent' : 'neutral'}>
                        {row.kindLabel}
                      </span>
                    </span>
                  </td>
                  <td data-label="Item">
                    <span className="si-subject">{row.subject}</span>
                    {where && <span className="si-where">{where}</span>}
                  </td>
                  <td data-label="State">
                    <span className="si-state">
                      <StatusChip tone={rowStateTone(row)}>{row.stateLabel}</StatusChip>
                      <span className="si-lifecycle">
                        {row.lifecycle.label}
                        {row.lifecycle.detail ? ` · ${row.lifecycle.detail}` : ''}
                      </span>
                    </span>
                  </td>
                  <td data-label="Waiting on">{waitingLabel(row)}</td>
                  <td data-label="Last activity">
                    <span title={absoluteTime(row.lastActivityAt)}>
                      {relativeTime(row.lastActivityAt)}
                    </span>
                  </td>
                  <td data-label="Signals">
                    <SignalChips counts={row.signals} />
                  </td>
                  {renderRowActions && (
                    <td className="col-action" onClick={(e) => e.stopPropagation()}>
                      {renderRowActions(row)}
                    </td>
                  )}
                </tr>
                {isExpanded && renderExpanded && (
                  <tr className="si-row-expanded">
                    <td colSpan={columnCount}>{renderExpanded(row)}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
